import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { IAllowlistModel } from "@/Common/Models/Administration/IAllowlistModel";
import { Guid } from "@/Common/Models/Guid";
import { Person } from "@/Common/Models/Person/Person";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { AllowlistType } from "@/Common/Models/UI/Enums";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { PendingApprovalNoteModel } from "@/Patient/Api/Dto/Responses/PendingApprovalNoteModel";

import img_locked from "@/Globals/_images/locked.png";
import img_unlocked from "@/Globals/_images/unlocked.png";

interface IResidentApprovalPopup {
    onCloseCallback: () => void;
    EncounterIdSearchPendingApprouval?: string;
    ShowMessageBox: boolean;
    title: string;

    onSelect: (selectedSupervisorContactId: string, message: string, allowListChanges: IAllowlistModel) => void;
    SelectButtonLabel: string;
    SupervisorSelectionIsOptional?: boolean;
    allowlist: IAllowlistModel;
}

class ResidentApprovalPopupState {
    public selectedSupervisorContactId: string;
    public selectedSupervisorName: string;
    public message: string;
    public supervisors: Person[];
    public isSupervisorLocked: boolean;
    public PendingApprovalNotes: Immutable.List<PendingApprovalNoteModel>;
    public isLoading: boolean;

    public shouldBeAddedToAllowList: boolean;
}

class ResidentApprovalPopup extends BaseReactComponent<IResidentApprovalPopup, ResidentApprovalPopupState> {
    private id = "residentapprovalpopup";

    constructor(props: IResidentApprovalPopup) {
        super(props);

        const selectedSupervisorContactId = sessionStore.lockedSupervisorContactId;
        const isSupervisorLocked = !IsNullOrWhiteSpace(selectedSupervisorContactId);

        let initialState = new ResidentApprovalPopupState();
        initialState.isSupervisorLocked = isSupervisorLocked;
        initialState.selectedSupervisorContactId = selectedSupervisorContactId;
        initialState.PendingApprovalNotes = [].toImmutable();

        if (isSupervisorLocked) {
            initialState.selectedSupervisorName = cacheStore.GetCliniciansPersonsByContactId(selectedSupervisorContactId).NameWithPrefix;
            initialState.shouldBeAddedToAllowList = this.getShouldBeAddedToAllowlist(cacheStore.GetEmployeeByContactIdOrEmpty(selectedSupervisorContactId).ContactFacet_id);
        }

        initialState.message = "";
        initialState.supervisors = cacheStore.Clinicians.filter(clinician => {
            if (Employee.getIssuingOrgRule(clinician.EmployeeModel)) {
                const title = Employee.getIssuingOrgRule(clinician.EmployeeModel).Title;

                return EmployeeTitleHelper.canSupervise(title);
            }
            return false;
        });
        initialState.isLoading = false;
        this.state = initialState;
    }

    public componentDidMount(): void {
        if (!IsNullOrWhiteSpace(this.props.EncounterIdSearchPendingApprouval)) {
            this.mutateState(s => s.isLoading = true);
            Client.Post(
                ServerOperations.Server.Myle_Resident_CheckApprovalStatus,
                { EncounterId: this.props.EncounterIdSearchPendingApprouval },
                response => {
                    const supervisorId: string = response.SupervisorId == "00000000-0000-0000-0000-000000000000" ? null : response.SupervisorId;
                    const notesList: PendingApprovalNoteModel[] = response.PendingNoteList != null ? response.PendingNoteList : [];
                    if (!IsNullOrWhiteSpace(supervisorId)) {
                        this.mutateState(s => {
                            s.isLoading = false;
                            s.PendingApprovalNotes = Immutable.List(notesList);
                            s.selectedSupervisorContactId = supervisorId;
                            s.isSupervisorLocked = sessionStore.lockedSupervisorContactId == supervisorId;
                            s.selectedSupervisorName = cacheStore.GetCliniciansPersonsByContactId(supervisorId).NameWithPrefix;
                        });
                    }
                    else
                        this.mutateState(s => s.isLoading = false);
                },
                () => this.props.onCloseCallback()
            );
        }

        const elem = $("#" + this.id);
        const container = document.body;
        const dragOptions = {
            containment: container,
            handle: ".pa-header"
        };
        elem.draggable(dragOptions);
    }

    public componentWillUnmount(): void {
        sessionStore.hideGlobalOverlay();
        const elem = $("#" + this.id);
        elem.draggable("destroy");
    }

    public render(): JSX.Element {
        return (
            <div id={this.id} onClick={e => e.stopPropagation()} className="pending-approval-popup" style={{ overflow: "visible" }}>
                <div className="pa-header">
                    <div className="flex-container">
                        <h4>{this.props.title}</h4>
                        <div className="text-right">
                            <a className="close" onClick={() => this.props.onCloseCallback()} aria-label="Close">×</a>
                        </div>
                    </div>
                </div>

                {!this.state.isLoading && (
                    <div className="pa-main">
                        <div className="row">
                            <div className="col-lg-2 col-md-2 col-sm-2 text-right">
                                <label className="label-input">{cacheStore.GetString("SupervisorLabel")}</label>
                            </div>
                            <div className="col-lg-10 col-md-10 col-sm-10">
                                {!IsNullOrWhiteSpace(this.state.selectedSupervisorContactId) && (
                                    <div className="flex-container">
                                        <div>
                                            <label className="label-input">{this.state.selectedSupervisorName}</label> <span style={{ marginLeft: "5px" }} onClick={() => this.handleRemoveSelectedClinician()} className="glyphicon glyphicon-remove red-text pointer push-right" />
                                        </div>
                                        <div>
                                            {this.state.isSupervisorLocked === false && (<img style={{ height: "40px", width: "42px", paddingBottom: "5px" }} src={img_unlocked} alt={cacheStore.GetString("RememberLabel")} onClick={() => this.handleLockClick()} />)}
                                            {this.state.isSupervisorLocked === true && (<img style={{ height: "40px", width: "42px", paddingBottom: "5px" }} src={img_locked} alt={cacheStore.GetString("RememberLabel")} onClick={() => this.handleUnlockClick()} />)}
                                        </div>
                                    </div>
                                )}
                                {IsNullOrWhiteSpace(this.state.selectedSupervisorContactId) && (
                                    <div className="flex-container">
                                        <div style={{ width: "100%" }}>
                                            <TextboxControl
                                                autoCompleteItems={this.state.supervisors.map(x => new SearchableItemModelT<Person>(x.NameWithPrefix, x.ContactId, x)).toImmutable()}
                                                onAutoCompleteSelectCallBack={value => this.handleSupervisorSelected(value)}
                                                onAutoCompleteSelectClearTextBox={true}
                                                value={this.state.selectedSupervisorName}
                                                dataCy="residentApproval-supervisor"
                                            />
                                        </div>
                                    </div>
                                )}
                                {this.state.shouldBeAddedToAllowList && (
                                    <span className="label-error">
                                        {cacheStore.GetString("AddToAllowListWarningLabel", {
                                            clinicianName: this.state.selectedSupervisorName
                                        })}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="note-pa-render">
                            {this.state.PendingApprovalNotes.toArray().map((note, idx) => (
                                <div key={"_n" + idx}>
                                    <div>
                                        <strong>
                                            {cacheStore.GetCliniciansPersonsByContactId(note.EmployeeContactId).NameWithPrefix}
                                        </strong>
                                        <span style={{ color: "#777", fontSize: ".92em", marginLeft: "5px" }}>
                                            {moment(note.CreationDate).format("YYYY/MM/DD") + " " + cacheStore.GetString("LowerCaseAtLabel") + " " + moment(note.CreationDate).format("hh:mm:ss")}
                                        </span>
                                    </div>
                                    <div>
                                        <p>{JSXHelper.BreakStringIntoParagraphs(note.Note)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {this.props.ShowMessageBox && (
                            <div>
                                <label>Message</label>
                                <textarea
                                    className="form-control"
                                    id="rap_note"
                                    data-cy="residentApproval-message"
                                    value={this.state.message}
                                    onChange={(e: any) => this.handleMessageChanged(e.target.value)}
                                    rows={4}
                                />
                            </div>
                        )}
                    </div>
                )}

                <div className="pa-footer">
                    <div className="text-right">
                        <button className="btn btn-default push-right-5" onClick={() => this.props.onCloseCallback()}>{cacheStore.GetString("CancelLabel")}</button>
                        <button className="btn btn-primary" disabled={!this.props.SupervisorSelectionIsOptional && IsNullOrWhiteSpace(this.state.selectedSupervisorContactId)} onClick={() => this.handleSendClick()} data-cy="residentApproval-send">
                            {this.props.SelectButtonLabel}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    private handleMessageChanged(text: string): void {
        this.mutateState(s => s.message = text);
    }

    private handleSupervisorSelected(contactId: string): void {
        const selectedContactFacetId = cacheStore.GetEmployeeByContactIdOrEmpty(contactId).ContactFacet_id;

        this.mutateState(s => {
            s.selectedSupervisorContactId = contactId;
            s.selectedSupervisorName = cacheStore.GetCliniciansPersonsByContactId(contactId).NameWithPrefix;
            s.shouldBeAddedToAllowList = this.getShouldBeAddedToAllowlist(selectedContactFacetId);
        });
    }

    private handleRemoveSelectedClinician(): void {
        this.mutateState(s => {
            s.selectedSupervisorContactId = null;
            s.selectedSupervisorName = "";
            s.isSupervisorLocked = false;
            s.shouldBeAddedToAllowList = false;

            sessionStore.lockedSupervisorContactId = "";
        });
    }

    private handleSendClick(): void {
        this.props.onSelect(this.state.selectedSupervisorContactId, this.state.message, this.getAllowListChanges());
    }

    private handleLockClick(): void {
        this.mutateState(s => {
            s.isSupervisorLocked = true;
            sessionStore.lockedSupervisorContactId = s.selectedSupervisorContactId;
        });
    }

    private handleUnlockClick(): void {
        this.mutateState(s => {
            s.isSupervisorLocked = false;
            sessionStore.lockedSupervisorContactId = "";
        });
    }

    private getAllowListChanges(): IAllowlistModel {
        if (!this.state.shouldBeAddedToAllowList)
            return null;

        const selectedContactFacetId = cacheStore.GetEmployeeByContactIdOrEmpty(this.state.selectedSupervisorContactId).ContactFacet_id;

        let allowListChanges = this.props.allowlist ?? {
            EmployeeId: sessionStore.getCurrentEmployee().ContactFacetId,
            AllowlistType: AllowlistType.MedicalNoteAllowlist,
            MemberIds: null,
            IsActive: null
        };

        const newMembers = isArrayNullOrUndefinedOrEmpty(allowListChanges.MemberIds) ?
            [selectedContactFacetId]
            : [...(allowListChanges.MemberIds), selectedContactFacetId];

        allowListChanges.MemberIds = newMembers;

        return allowListChanges;
    }

    private getShouldBeAddedToAllowlist(employeeContactFacetId: Guid): boolean {
        if (!cacheStore.ClinicSettings.EnableMedicalNoteProtection)
            return false;

        if (this.props.allowlist?.IsActive) {
            return !this.props.allowlist?.MemberIds?.any(allowlistMemberContactFacetId => allowlistMemberContactFacetId === employeeContactFacetId);
        }

        return false;
    }
}

export { ResidentApprovalPopup };
